import { FC, useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useIntl } from "react-intl";

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";

import type { IChartDataSignal } from "../../../scheme/components/analytics/interfaces";
import { isValidOrbitBandpassHz } from "../../../scheme/components/analytics/utils/orbit-harmonics";
import {
  estimateSignalSamplingRateHz,
  getSignalFilterComponents,
  getSignalFilterFrequencyBounds,
  isSynchronousSignal,
} from "../../utils/signal-filter";

interface ISignalFilterDialogProps {
  open: boolean;
  data: readonly IChartDataSignal[];
  appliedHarmonics?: readonly number[];
  appliedBandpassHz?: { lo: number; hi: number } | null;
  onClose: () => void;
  onApply: (harmonics: readonly number[], bandpassHz: { lo: number; hi: number } | null) => void;
  onClear: () => void;
}

const EMPTY_HARMONICS: readonly number[] = [];

const formatFrequency = (value: number): string =>
  Number.isInteger(value) ? String(value) : value.toFixed(value >= 100 ? 2 : 4);

const parseFrequency = (value: string): number => Number(value.trim().replace(",", "."));

export const SignalFilterDialog: FC<ISignalFilterDialogProps> = ({
  open,
  data,
  appliedHarmonics,
  appliedBandpassHz,
  onClose,
  onApply,
  onClear,
}) => {
  const { formatMessage } = useIntl();
  const normalizedAppliedHarmonics = appliedHarmonics ?? EMPTY_HARMONICS;
  const normalizedAppliedBandpassHz = appliedBandpassHz ?? null;
  const signal = data[0];
  const samplingRateHz = estimateSignalSamplingRateHz(signal);
  const frequencyBounds = getSignalFilterFrequencyBounds(signal);
  const synchronousSignal = isSynchronousSignal(signal);
  const components = useMemo(() => getSignalFilterComponents(signal), [signal]);
  const [draftHarmonics, setDraftHarmonics] = useState<number[]>([]);
  const [bandpassEnabled, setBandpassEnabled] = useState(false);
  const [bandpassLo, setBandpassLo] = useState("");
  const [bandpassHi, setBandpassHi] = useState("");
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setDraftHarmonics([...normalizedAppliedHarmonics]);
    setBandpassEnabled(normalizedAppliedBandpassHz != null);
    setBandpassLo(normalizedAppliedBandpassHz ? String(normalizedAppliedBandpassHz.lo) : "");
    setBandpassHi(normalizedAppliedBandpassHz ? String(normalizedAppliedBandpassHz.hi) : "");
    setError(false);
  }, [normalizedAppliedBandpassHz, normalizedAppliedHarmonics, open]);

  const handleBandpassEnabledChange = (event: ChangeEvent<HTMLInputElement>) => {
    setBandpassEnabled(event.target.checked);
    if (event.target.checked) {
      setDraftHarmonics([]);
    }
  };

  const handleApply = () => {
    if (bandpassEnabled) {
      const lo = parseFrequency(bandpassLo);
      const hi = parseFrequency(bandpassHi);
      if (samplingRateHz == null || !isValidOrbitBandpassHz(samplingRateHz, lo, hi, signal?.items.length)) {
        setError(true);
        return;
      }
      onApply([], { lo, hi });
      return;
    }
    onApply(draftHarmonics, null);
  };

  const handleClear = () => {
    setDraftHarmonics([]);
    setBandpassEnabled(false);
    setBandpassLo("");
    setBandpassHi("");
    setError(false);
    onClear();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{formatMessage({ id: "pages.scheme.analytics.signalFilterDialogTitle" })}</DialogTitle>
      <DialogContent>
        {error ? (
          <Typography variant="body2" color="error" sx={{ mb: 1 }}>
            {formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterInvalid" })}
          </Typography>
        ) : null}

        {samplingRateHz != null && frequencyBounds ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              {formatMessage({ id: "pages.scheme.analytics.orbitBandpassFilterSection" })}
            </Typography>
            <FormControlLabel
              control={<Checkbox size="small" checked={bandpassEnabled} onChange={handleBandpassEnabledChange} />}
              label={formatMessage({ id: "pages.scheme.analytics.signalBandpassFilterEnable" })}
            />
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                label={formatMessage({
                  id: synchronousSignal
                    ? "pages.scheme.analytics.signalBandpassFilterLoOrders"
                    : "pages.scheme.analytics.orbitBandpassFilterLo",
                })}
                value={bandpassLo}
                onChange={event => setBandpassLo(event.target.value)}
                disabled={!bandpassEnabled}
              />
              <TextField
                size="small"
                label={formatMessage({
                  id: synchronousSignal
                    ? "pages.scheme.analytics.signalBandpassFilterHiOrders"
                    : "pages.scheme.analytics.orbitBandpassFilterHi",
                })}
                value={bandpassHi}
                onChange={event => setBandpassHi(event.target.value)}
                disabled={!bandpassEnabled}
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
              {formatMessage(
                {
                  id: synchronousSignal
                    ? "pages.scheme.analytics.signalBandpassFilterRangeOrders"
                    : "pages.scheme.analytics.orbitBandpassFilterFieldRangeHelper",
                },
                { fMin: formatFrequency(frequencyBounds.fMin), fMax: formatFrequency(frequencyBounds.fMax) },
              )}
            </Typography>
          </Box>
        ) : null}

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          {formatMessage({ id: "pages.scheme.analytics.signalHarmonicFilterTitle" })}
        </Typography>
        {!synchronousSignal && (signal?.rotationFrequencyRpm ?? 0) <= 0 ? (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: "pages.scheme.analytics.signalHarmonicFilterDisabled" })}
          </Typography>
        ) : components.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {formatMessage({ id: "pages.scheme.analytics.orbitDesktopComponentsEmpty" })}
          </Typography>
        ) : (
          <TableContainer sx={{ mt: 1, maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterColumnHarm" })}</TableCell>
                  <TableCell align="right">
                    {formatMessage({ id: "pages.scheme.analytics.signalHarmonicFilterColumnAmplitude" })}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {components.map(component => (
                  <TableRow key={component.binIndex} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={draftHarmonics.includes(component.harmonic)}
                        disabled={bandpassEnabled}
                        onChange={() => {
                          setBandpassEnabled(false);
                          setDraftHarmonics(current =>
                            current.includes(component.harmonic)
                              ? current.filter(value => value !== component.harmonic)
                              : [...current, component.harmonic],
                          );
                        }}
                      />
                    </TableCell>
                    <TableCell>{formatFrequency(component.harmonic)}</TableCell>
                    <TableCell align="right">{component.amplitude.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClear} sx={{ mr: "auto" }} color="inherit">
          {formatMessage({ id: "form.buttons.clear" })}
        </Button>
        <Button onClick={onClose}>{formatMessage({ id: "form.buttons.cancel" })}</Button>
        <Button variant="contained" onClick={handleApply}>
          {formatMessage({ id: "pages.scheme.analytics.orbitHarmonicFilterApply" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
